/**
 * Per-account allow-lists of assessment types that may generate a
 * claim. Two of them exist, with identical rules and different gates:
 *
 *   - `intellyChartAllowedSurveys` — Preventive Health Counseling
 *     (CPT 99401), offered on assessments the catalogue marks with
 *     `hasPreventiveCounseling` (API PR 2422).
 *   - `programClaimAllowedSurveys` — Program / E-Visit (CPT 99422),
 *     offered on assessments with `hasAutomation`, since a 99422 needs
 *     a resource the automation sends (API 2026-09-24).
 *
 * Contract, the same for both:
 *   - a flat `string[]` of assessment keys, the vocabulary of
 *     `allowedSurveys[].key` and the catalogue ids; the server matches
 *     case-insensitively.
 *   - every entry MUST also be in `allowedSurveys`, or the whole PATCH
 *     is refused with a 422 keyed `<field>[<index>]`.
 *   - the server never auto-prunes: dropping an assessment from the
 *     Allowed Assessments list leaves it in both allow-lists, so the
 *     dashboard has to clear it in the same save.
 *   - an empty list means "no claims of that kind", not "all".
 *
 * The two differ in one place only, and it is deliberate. The
 * counseling list is emptied while Claims management is off, because
 * `accountPermissions.enableIntellyChart` is derived from it and the
 * two must not disagree. The E-Visit list has no such flag: it is
 * simply inert while Claims is off, so it is left exactly as stored
 * (confirmed with the backend 2026-09-24). Emptying it would wipe an
 * admin's choices — after the migration backfill, the entire Allowed
 * Assessments list.
 */

export const CLAIM_ALLOW_LIST_FIELDS = {
  counseling: 'intellyChartAllowedSurveys',
  programClaim: 'programClaimAllowedSurveys',
};

const lower = value => String(value ?? '').toLowerCase();

export const allowedSurveyKeys = allowedSurveys =>
  new Set(
    (allowedSurveys || [])
      .map(item => (typeof item === 'object' && item !== null ? item.key : item))
      .filter(Boolean)
      .map(lower)
  );

/**
 * Keep only the keys that are still in the Allowed Assessments list.
 * Order and original casing are preserved.
 */
export const pruneClaimAllowList = (keys, allowedSurveys) => {
  const allowed = allowedSurveyKeys(allowedSurveys);
  return (keys || []).filter(key => allowed.has(lower(key)));
};

/** Case-insensitive membership test used by the picker's checkboxes. */
export const hasClaimAllowListKey = (keys, key) =>
  (keys || []).some(item => lower(item) === lower(key));

/** Matches the `<field>[<index>]` paths the server errors are keyed by. */
export const claimAllowListErrorRegex = field => new RegExp(`^${field}\\[(\\d+)\\]$`);

/**
 * Turn `{ "<field>[1]": msg }` backend errors into
 * `{ <assessment key>: msg }` so the picker can flag the exact row.
 * Errors whose index no longer exists are dropped.
 */
export const claimAllowListRowErrors = (backendErrors, keys, field) => {
  const regex = claimAllowListErrorRegex(field);
  const result = {};
  Object.entries(backendErrors || {}).forEach(([path, message]) => {
    const match = regex.exec(path);
    if (!match) return;
    const key = (keys || [])[Number(match[1])];
    if (key) result[key] = message;
  });
  return result;
};

/** True for any path addressing either allow-list, row-indexed or not. */
export const isClaimAllowListPath = path =>
  Object.values(CLAIM_ALLOW_LIST_FIELDS).some(
    field => path === field || claimAllowListErrorRegex(field).test(path)
  );
